import { type ComputedRef, computed } from "vue";
import { AppConstants } from "@/common";
import type { IButtonsIds } from "@/components/Player/components/ControlsPanel/types";

interface IProps {
    getBottomId(line: string): string;
    getTopId(line: string): string;
    getLeftId(currentId: string): string | null;
    getRightId(currentId: string): string | null;
    isActive(id: string): boolean;
    isVisibleDescription: ComputedRef<boolean>;
    isFavorite: ComputedRef<boolean>;
    isPlaying: ComputedRef<boolean>;
    buttonsIds: IButtonsIds;
}

interface IButtonAttrs {
    id: IButtonsIds[keyof IButtonsIds];
    dataType?: string;
    dataKeydown?: string | null;
    dataKeyright?: string | null;
    dataKeyleft?: string | null;
    dataKeyup?: string;
    isActive?: boolean;
    iconAttrs?: {
        iconName: string;
        [x: string]: string | boolean;
    };
};

interface IExportInterface {
    buttonsIds: IButtonsIds;
    infoBtn: ComputedRef<IButtonAttrs>;
    infoUpBtn: ComputedRef<IButtonAttrs>;
    infoDownBtn: ComputedRef<IButtonAttrs>;
    addBtn: ComputedRef<IButtonAttrs>;
    subtitlesBtn: ComputedRef<IButtonAttrs>;
    progressBtn: ComputedRef<IButtonAttrs>;
    navBackBtn: ComputedRef<IButtonAttrs>;
    restartBtn: ComputedRef<IButtonAttrs>;
    skipBackBtn: ComputedRef<IButtonAttrs>;
    playBtn: ComputedRef<IButtonAttrs>;
    skipForwardBtn: ComputedRef<IButtonAttrs>;
    navForwardBtn: ComputedRef<IButtonAttrs>;
    liveBtn: ComputedRef<IButtonAttrs>;
};

export const useGetButtonsData = ({
    getBottomId, getTopId, getLeftId, getRightId, isActive, isVisibleDescription, isFavorite, isPlaying, buttonsIds
}: IProps ) => {

    const infoBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnInfo,
        dataType: "hide-button",
        dataKeydown: getBottomId('top'),
        dataKeyright: buttonsIds.btnAdd,
        dataKeyleft: buttonsIds.btnInfoDown,
        isActive: isActive(buttonsIds.btnInfo),
        iconAttrs: {
            showDescriptions: isVisibleDescription.value,
            iconName: "IconInfo"
        }
    }));

    const infoUpBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnInfoUp,
        dataType: "hide-button",
        dataKeydown: buttonsIds.btnInfoDown,
        dataKeyright: buttonsIds.btnInfo,
        isActive: isActive(buttonsIds.btnInfoUp),
        iconAttrs: {
            iconName: "IconUp"
        }
    }));

    const infoDownBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnInfoDown,
        dataType: "hide-button",
        dataKeydown: getBottomId('top'),
        dataKeyup: buttonsIds.btnInfoUp,
        dataKeyright: buttonsIds.btnInfo,
        isActive: isActive(buttonsIds.btnInfoDown),
        iconAttrs: {
            iconName: "IconDown"
        }
    }));

    const addBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnAdd,
        dataType: "hide-button",
        dataKeydown: getBottomId('top'),
        dataKeyright: buttonsIds.btnSubtitles,
        dataKeyleft: buttonsIds.btnInfo,
        isActive: isActive(buttonsIds.btnAdd),
        iconAttrs: {
            isFavorite: isFavorite.value,
            iconName: "IconMyTv"
        }
    }));

    const subtitlesBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnSubtitles,
        dataType: "hide-button",
        dataKeydown: getBottomId('top'),
        dataKeyleft: buttonsIds.btnAdd,
        isActive: isActive(buttonsIds.btnSubtitles),
        iconAttrs: {
            iconName: "IconSubtitle"
        }
    }));

    const progressBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnProgress,
        dataKeydown: getBottomId(buttonsIds.btnProgress),
        dataKeyup: getTopId(buttonsIds.btnProgress),
    }));

    const navBackBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnNavBack,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyright: getRightId(buttonsIds.btnNavBack),
        isActive: isActive(buttonsIds.btnNavBack),
        iconAttrs: {
            iconName: "IconNav",
            isNavPrevious: true
        }
    }));

    const restartBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnRestart,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyleft: getLeftId(buttonsIds.btnRestart),
        dataKeyright: getRightId(buttonsIds.btnRestart),
        isActive: isActive(buttonsIds.btnRestart),
        iconAttrs: {
            iconName: "IconRestart"
        }
    }));

    const skipBackBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnSkipBack,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyleft: getLeftId(buttonsIds.btnSkipBack),
        dataKeyright: getRightId(buttonsIds.btnSkipBack),
        isActive: isActive(buttonsIds.btnSkipBack),
        iconAttrs: {
            isSkipPrevious: true,
            iconName: "IconSkip"
        }
    }));

    const playBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnPlay,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyleft: getLeftId(buttonsIds.btnPlay),
        dataKeyright: getRightId(buttonsIds.btnPlay),
        isActive: isActive(buttonsIds.btnPlay),
        iconAttrs: {
            isPlay: !isPlaying.value,
            iconName: "IconPlay"
        }
    }));

    const skipForwardBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnSkipForward,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyleft: getLeftId(buttonsIds.btnSkipForward),
        dataKeyright: getRightId(buttonsIds.btnSkipForward),
        isActive: isActive(buttonsIds.btnSkipForward),
        iconAttrs: {
            isSkipPrevious: false,
            iconName: "IconSkip"
        }
    }));

    const navForwardBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnNavForward,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyleft: getLeftId(buttonsIds.btnNavForward),
        dataKeyright: getRightId(buttonsIds.btnNavForward),
        isActive: isActive(buttonsIds.btnNavForward),
        iconAttrs: {
            isNavPrevious: false,
            iconName: "IconNav"
        }
    }));

    const liveBtn = computed((): IButtonAttrs => ({
        id: buttonsIds.btnLive,
        dataKeydown: AppConstants.shelfKeys.playerLinesId,
        dataKeyup: getTopId('controls'),
        dataKeyleft: getLeftId(buttonsIds.btnLive),
        isActive: isActive(buttonsIds.btnLive),
        iconAttrs: {
            iconName: "IconLive"
        }
    }));

    return <IExportInterface> {
        buttonsIds,
        infoBtn,
        infoUpBtn,
        infoDownBtn,
        addBtn,
        subtitlesBtn,
        progressBtn,
        navBackBtn,
        restartBtn,
        skipBackBtn,
        playBtn,
        skipForwardBtn,
        navForwardBtn,
        liveBtn
    };
};
